import { v4 as uuidv4 } from 'uuid';
import {
  calculateInitialColumnWidths,
  defaultRowMeta,
  defaultColumnMeta,
  initialColumnWidth,
  initialPricingColumnWidth,
} from './variables';
import type { TableColumnType, TableRowType, TableType } from 'components/editor/grid/reduxStore/table.types';
import {
  TableTypeIdentifier,
  ColumnTypeIdentifier,
  TotalRowTypesIdentifier,
  RowTypesIdentifier,
} from 'components/editor/grid/reduxStore/table.types';
import { TableDefaultClassNamesEnum } from '../../../grid/reduxStore/textDefaultStylingSlice';

export const DEFAULT_DECIMAL_PLACES = 2;
export const DOLLAR_SIGN = '$';
export const PERCENT_SIGN = '%';
export const PERCENT_REFERENCE = 'percent';
export const CURRENCY_REFERENCE = 'currency';
export const HIDE_CELL_SIGN = 'Hide cell';
export const HIDE_CELL_CODE = 'hide_cell';
export const MIN_COLUMN_WIDTH = 52;

export const cellMenu = [
  { sign: DOLLAR_SIGN, value: 'document.grid.table.formater_flat', code: CURRENCY_REFERENCE },
  { sign: PERCENT_SIGN, value: 'document.grid.table.formater_percent', code: PERCENT_REFERENCE },
];

export const optionalColumn: TableColumnType = {
  field: 'optionalColumn',
  headerName: '',
  width: 50,
  editable: false,
  columnType: ColumnTypeIdentifier.OPTIONAL,
  type: 'string',
  headerAlign: 'center',
  resizable: false,
  activeHoverColumn: false,
};

const commonColumnProperties: TableColumnType = {
  field: 'id',
  headerName: 'ID',
  editable: false,
  columnType: ColumnTypeIdentifier.HANDLER,
  activeHoverColumn: false,
};

const commonTextColumnProperties: TableColumnType = {
  field: 'column1',
  headerName: 'Text',
  editable: true,
  headerAlign: 'left',
  columnType: ColumnTypeIdentifier.TEXT,
  type: 'string',
  activeHoverColumn: false,
};

export const columns: TableColumnType[] = [
  commonColumnProperties,
  { ...commonTextColumnProperties, width: initialColumnWidth },
  {
    ...defaultColumnMeta,
    field: 'column2',
    width: initialColumnWidth,
  },
];

export const rows: TableRowType[] = [
  {
    id: uuidv4(),
    column1: `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_HEADER}">Text</p>`,
    column2: `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_HEADER}">Text</p>`,
    rowType: RowTypesIdentifier.HEADER,
    ...defaultRowMeta,
  },
  {
    id: uuidv4(),
    column1: `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_BODY}"></p>`,
    column2: `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_BODY}"></p>`,
    rowType: RowTypesIdentifier.BODY,
    ...defaultRowMeta,
  },
  {
    id: uuidv4(),
    column1: `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_BODY}"></p>`,
    column2: `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_BODY}"></p>`,
    rowType: RowTypesIdentifier.BODY,
    ...defaultRowMeta,
  },
];

export const pricingColumns: TableColumnType[] = [
  commonColumnProperties,
  { ...commonTextColumnProperties, width: initialPricingColumnWidth },
  {
    field: 'price',
    headerName: 'Price',
    width: initialPricingColumnWidth,
    align: 'right',
    headerAlign: 'right',
    editable: true,
    columnType: ColumnTypeIdentifier.PRICE,
    type: 'number',
    activeHoverColumn: false,
  },
  {
    field: 'column2',
    headerName: 'Multiplier',
    width: initialPricingColumnWidth,
    align: 'right',
    headerAlign: 'right',
    editable: true,
    columnType: ColumnTypeIdentifier.MULTIPLIER,
    type: 'number',
    activeHoverColumn: false,
  },
  {
    field: 'subtotal',
    headerName: 'Subtotal',
    width: initialPricingColumnWidth,
    align: 'right',
    headerAlign: 'right',
    editable: true,
    columnType: ColumnTypeIdentifier.SUBTOTAL,
    type: 'number',
    activeHoverColumn: false,
  },
];

export const getDefaultHTMLForSubtotalRowTitle = (rowType: TotalRowTypesIdentifier): string => {
  return `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_SUBTOTAL}">${rowType}</p>`;
};

const defaultHTMLForTotalRowTitle = `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_TOTAL}">${TotalRowTypesIdentifier.TOTAL}</p>`;

export const setOfRowsForPricingTable = [
  {
    id: uuidv4(),
    title: getDefaultHTMLForSubtotalRowTitle(TotalRowTypesIdentifier.SUBTOTAL),
    rowType: TotalRowTypesIdentifier.SUBTOTAL,
    isRowHidden: false,
    ...defaultRowMeta,
    price: '',
    subtotal: '',
  },
  {
    id: uuidv4(),
    title: getDefaultHTMLForSubtotalRowTitle(TotalRowTypesIdentifier.TAX),
    rowType: TotalRowTypesIdentifier.TAX,
    cellConfig: {
      price: {
        valueFormatter: PERCENT_SIGN,
      },
    },
    optionsMenu: cellMenu,
    ...defaultRowMeta,
    price: '',
    subtotal: '',
  },
  {
    id: uuidv4(),
    title: getDefaultHTMLForSubtotalRowTitle(TotalRowTypesIdentifier.DISCOUNT),
    rowType: TotalRowTypesIdentifier.DISCOUNT,
    cellConfig: {
      price: {
        valueFormatter: PERCENT_SIGN,
      },
    },
    optionsMenu: cellMenu,
    ...defaultRowMeta,
    price: '',
    subtotal: '',
  },
  {
    id: uuidv4(),
    title: defaultHTMLForTotalRowTitle,
    rowType: TotalRowTypesIdentifier.TOTAL,
    ...defaultRowMeta,
    price: '',
    subtotal: '',
  },
];

export const setOfRowsForPricingTableWithReferences = [
  {
    id: uuidv4(),
    title: getDefaultHTMLForSubtotalRowTitle(TotalRowTypesIdentifier.SUBTOTAL),
    rowType: TotalRowTypesIdentifier.SUBTOTAL,
    isRowHidden: false,
    ...defaultRowMeta,
    price: '',
    subtotal: '',
  },
  {
    id: uuidv4(),
    title: getDefaultHTMLForSubtotalRowTitle(TotalRowTypesIdentifier.TAX),
    rowType: TotalRowTypesIdentifier.TAX,
    cellConfig: {
      price: {
        valueFormatter: PERCENT_REFERENCE,
      },
    },
    ...defaultRowMeta,
    price: '',
    subtotal: '',
  },
  {
    id: uuidv4(),
    title: getDefaultHTMLForSubtotalRowTitle(TotalRowTypesIdentifier.DISCOUNT),
    rowType: TotalRowTypesIdentifier.DISCOUNT,
    cellConfig: {
      price: {
        valueFormatter: PERCENT_REFERENCE,
      },
    },
    ...defaultRowMeta,
    price: '',
    subtotal: '',
  },
  {
    id: uuidv4(),
    title: defaultHTMLForTotalRowTitle,
    rowType: TotalRowTypesIdentifier.TOTAL,
    ...defaultRowMeta,
    price: '',
    subtotal: '',
  },
];

export const pricingRows: TableRowType[] = [
  {
    id: uuidv4(),
    column1: `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_HEADER}">Name</p>`,
    column2: `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_HEADER}">Quantity</p>`,
    price: `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_HEADER}">Price</p>`,
    subtotal: `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_HEADER}">Subtotal</p>`,
    rowType: RowTypesIdentifier.HEADER,
    isOptionalToggleAvailable: false,
    ...defaultRowMeta,
  },
  {
    id: uuidv4(),
    column1: `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_BODY}"></p>`,
    column2: '',
    price: '',
    subtotal: '',
    rowType: RowTypesIdentifier.BODY,
    isOptionalToggleAvailable: false,
    ...defaultRowMeta,
  },
  {
    id: uuidv4(),
    column1: `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_BODY}"></p>`,
    column2: '',
    price: '',
    subtotal: '',
    rowType: RowTypesIdentifier.BODY,
    isOptionalToggleAvailable: false,
    ...defaultRowMeta,
  },
  {
    id: uuidv4(),
    column1: `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_BODY}"></p>`,
    column2: '',
    price: '',
    subtotal: '',
    rowType: RowTypesIdentifier.BODY,
    isOptionalToggleAvailable: false,
    ...defaultRowMeta,
  },
  {
    id: uuidv4(),
    column1: `<p data-table-style="${TableDefaultClassNamesEnum.TABLE_BODY}"></p>`,
    column2: '',
    price: '',
    subtotal: '',
    rowType: RowTypesIdentifier.BODY,
    isOptionalToggleAvailable: false,
    ...defaultRowMeta,
  },
];

function applyProportionalWidths(cols: TableColumnType[], availableWidth?: number): TableColumnType[] {
  if (typeof availableWidth !== 'number') {
    return cols;
  }

  const visibleCount = cols.filter((col) => col.columnType !== ColumnTypeIdentifier.HANDLER).length;
  const widths = calculateInitialColumnWidths(visibleCount, availableWidth);

  if (widths.length === 0) {
    return cols;
  }

  let visibleIndex = 0;

  return cols.map((col) => {
    if (col.columnType === ColumnTypeIdentifier.HANDLER) {
      return col;
    }

    const nextCol = { ...col, width: widths[visibleIndex] };
    visibleIndex += 1;
    return nextCol;
  });
}

export function getDefaultTableContent(availableWidth?: number): TableType {
  return {
    rows,
    columns: applyProportionalWidths(columns, availableWidth),
    metadata: {
      tableType: TableTypeIdentifier.TEXT_TABLE,
      pricingDecimalPlaces: DEFAULT_DECIMAL_PLACES,
    },
  };
}

/** @deprecated Use {@link getDefaultTableContent} with an explicit availableWidth instead. Kept for backwards compatibility. */
export const defaultTableContent = getDefaultTableContent();

export function getDefaultPricingTableContent(isCurrencyActive = false, availableWidth?: number): TableType {
  return {
    rows: pricingRows,
    columns: applyProportionalWidths(pricingColumns, availableWidth),
    totalRows: isCurrencyActive ? setOfRowsForPricingTableWithReferences : setOfRowsForPricingTable,
    metadata: {
      tableType: TableTypeIdentifier.PRICING_TABLE,
      pricingDecimalPlaces: DEFAULT_DECIMAL_PLACES,
    },
  };
}
